import { QueryKey, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import ReportingFormDrawerV2 from "../components/Reporting/ReportingFormDrawerV2";
import { useTenantStore } from "../stores/tenantStore";
import { useUserStore } from "../stores/userStore";
import { TReportType } from "../types/reporting.types";
import {
	canWorkOnReport,
	canWorkOnReportItem,
	getReportEditLabel,
	REPORT_ITEM_LISTS,
	ReportItem,
	ReportItemTarget,
	ReportRecord,
	ReportViewer,
} from "../utils/reporting/reportEditAccess";
import { checkSuperAdminPriviledge, includesIgnoreCase } from "../utils/utils";

type DrawerState = {
	isOpen: boolean;
	// The item the drawer was opened on, if any.
	focusItem?: ReportItemTarget;
};

// Lets a report's page open the report in its drawer, for people who can
// change something in it now: open() shows the item list's tab, openItem()
// also opens one item. Closing the drawer refetches overviewQueryKey, so the
// page shows what was saved.
function useReportEditor(
	reportType: TReportType,
	report: ReportRecord | null | undefined,
	overviewQueryKey: QueryKey
) {
	const user = useUserStore((state) => state.user);
	const userRole = useUserStore((state) => state.userRole);
	const tenant = useTenantStore((state) => state.tenant);
	const isMasterTenant = useTenantStore((state) => state.isMasterTenant);
	const queryClient = useQueryClient();
	const reportId = report?._id;

	// Null until the drawer is first opened: a mounted drawer fetches its
	// lookup lists even while closed, so pages where nobody opens it don't
	// mount it. After that it stays mounted, closed, so it can slide out.
	const [drawerState, setDrawerState] = useState<DrawerState | null>(null);
	// Browser Back or Forward can show another report without remounting the
	// page. The drawer belongs to the report it was opened on, so it goes.
	const [drawerReportId, setDrawerReportId] = useState(reportId);
	if (drawerReportId !== reportId) {
		setDrawerReportId(reportId);
		setDrawerState(null);
	}

	const viewer: ReportViewer = {
		email: user?.email,
		isSuperAdmin: checkSuperAdminPriviledge(userRole),
		isTenantAdmin: includesIgnoreCase(tenant?.tenant_admin, user?.email),
		tenantKey: tenant?.role_key,
		isMasterTenant,
	};
	const list = REPORT_ITEM_LISTS[reportType];
	const canEdit = canWorkOnReport(reportType, report, viewer);
	const canEditItem = (item: ReportItem) =>
		canWorkOnReportItem(reportType, report, item, viewer);

	const open = () => setDrawerState({ isOpen: true });
	const openItem = (index: number) => {
		const number = list
			? report?.[list.name]?.[index]?.[list.numberField]
			: undefined;
		setDrawerState({ isOpen: true, focusItem: { index, number } });
	};
	const close = () => {
		setDrawerState({ isOpen: false });
		void queryClient.invalidateQueries({ queryKey: overviewQueryKey });
	};

	const isOpen = !!drawerState?.isOpen;
	// Mounted whether or not canEdit still holds, as a save in the drawer can
	// change it when the page refetches. activeId is only set while open: with
	// one, even a closed drawer fetches the report and follows its lock and
	// live updates. The key keeps it the same drawer when a page renders it in
	// another place, such as its error view after a failed refetch.
	const drawer =
		reportId && drawerState ? (
			<ReportingFormDrawerV2
				key="report-editor-drawer"
				isOpen={isOpen}
				reportType={reportType}
				activeId={isOpen ? reportId : undefined}
				initialTabKey={list?.tabKey}
				focusItem={drawerState.focusItem}
				onClose={close}
			/>
		) : null;

	return {
		canEdit,
		canEditItem,
		label: getReportEditLabel(reportType),
		open,
		openItem,
		drawer,
	};
}

export default useReportEditor;
